import { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Circle, Mail, RefreshCw, Workflow } from 'lucide-react';
import { ApiError, apiFetch } from '../../lib/api';
import { PageHeader, SectionCard, StatusBadge } from '../../components/WebPresentation';
import {
  buildCommunicationProfileSummaryDisplay,
  profileConfidenceTone,
  type CommunicationProfileSummaryResponse,
} from '../../lib/admin-communication-profile-summary';
import {
  buildCommunicationProfileCandidateDisplay,
  buildEngineJourneySummaryDisplay,
  watchDetailForNode,
  type CommunicationEngineNode as CommunicationNode,
  type CommunicationProfileCandidateAction,
  type CommunicationProfileCandidateSummary,
} from '../../lib/admin-communication-engine-journey';

type CommunicationNodesResponse = {
  nodes: CommunicationNode[];
};

type ProfileCandidateActionResponse = {
  candidate: CommunicationProfileCandidateSummary | null;
  action: CommunicationProfileCandidateAction;
  previousStatus: CommunicationProfileCandidateSummary['status'];
  newStatus: CommunicationProfileCandidateSummary['status'];
  changed: boolean;
};

type ExpectedNode = {
  kind: string;
  title: string;
  priority: string;
  visibility: string;
  dueHint?: string | null;
};

type TimelineTemplate = {
  key: string;
  label: string;
  expectedMainNodes: ExpectedNode[];
};

type IndustryProfile = {
  key: string;
  label: string;
  engineCard?: {
    timelineTemplates?: TimelineTemplate[];
  };
};

type IndustryProfilesResponse = {
  selectedProfileKey: string;
  selectedProfile: IndustryProfile;
};

type StaffSetupResponse = {
  googleWorkspace: { configured: boolean };
  rows: Array<{
    staffProfileId: string;
    label: string;
    googleWorkspaceIngestion: {
      gmailConnected: boolean;
      calendarConnected: boolean;
      ingestionActive: boolean;
      gmail: { livePollingAvailable: boolean; lastSyncedAt: string | null; status: string };
      calendar: { livePollingAvailable: boolean; lastSyncedAt: string | null; status: string };
    };
  }>;
};

type CommunicationsState = {
  nodes: CommunicationNode[];
  industry: IndustryProfilesResponse | null;
  staffSetup: StaffSetupResponse | null;
  profileSummary: CommunicationProfileSummaryResponse | null;
};

export default function CommunicationsPage() {
  const [data, setData] = useState<CommunicationsState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [candidateActionPending, setCandidateActionPending] = useState<CommunicationProfileCandidateAction | null>(null);
  const [candidateActionError, setCandidateActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [nodes, industry, staffSetup, profileSummary] = await Promise.all([
        apiFetch<CommunicationNodesResponse>('/api/admin/communication-nodes?limit=25'),
        apiFetch<IndustryProfilesResponse>('/api/admin/settings/industry-profiles'),
        apiFetch<StaffSetupResponse>('/api/admin/settings/staff-profile-setup-completeness'),
        apiFetch<CommunicationProfileSummaryResponse>('/api/admin/communication-profile-summary?limit=25').catch(() => null),
      ]);
      setData({ nodes: nodes.nodes ?? [], industry, staffSetup, profileSummary });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load communications.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const firstNode = useMemo(() => {
    return [...(data?.nodes ?? [])].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())[0] ?? null;
  }, [data?.nodes]);

  const selectedProfile = data?.industry?.selectedProfile ?? null;
  const playbookActive = Boolean(selectedProfile);
  const playbookHelpedSummary = Boolean(firstNode?.playbookNode);
  const profileSummaryDisplay = buildCommunicationProfileSummaryDisplay(data?.profileSummary);
  const profileCandidateDisplay = buildCommunicationProfileCandidateDisplay(data?.nodes ?? []);
  const engineJourneyDisplay = buildEngineJourneySummaryDisplay(data?.nodes ?? []);


  const applyProfileCandidateAction = useCallback(async (action: CommunicationProfileCandidateAction) => {
    const candidateId = profileCandidateDisplay.actionCandidateId;
    if (!candidateId) return;
    setCandidateActionPending(action);
    setCandidateActionError(null);
    try {
      const result = await apiFetch<ProfileCandidateActionResponse>(`/api/admin/communication-profile-candidates/${encodeURIComponent(candidateId)}/actions`, {
        method: 'POST',
        body: { action },
      });
      if (result.candidate) {
        setData(current => current ? {
          ...current,
          nodes: current.nodes.map(node => node.profileCandidate?.candidateId === result.candidate?.candidateId
            ? { ...node, profileCandidate: result.candidate }
            : node),
        } : current);
      }
    } catch (err) {
      setCandidateActionError(err instanceof ApiError ? err.message : 'Could not update candidate status.');
    } finally {
      setCandidateActionPending(null);
    }
  }, [profileCandidateDisplay.actionCandidateId]);

  const template = useMemo(() => {
    const templates = selectedProfile?.engineCard?.timelineTemplates ?? [];
    const key = firstNode?.playbookNode?.industryTimelineTemplateKey;
    return templates.find(item => item.key === key) ?? templates[0] ?? null;
  }, [firstNode?.playbookNode?.industryTimelineTemplateKey, selectedProfile?.engineCard?.timelineTemplates]);

  const playbookNodes = useMemo(() => {
    return [...(data?.nodes ?? [])]
      .filter(node => node.playbookNode)
      .sort((a, b) => new Date(a.lastEvidenceAt ?? a.createdAt ?? a.updatedAt).getTime() - new Date(b.lastEvidenceAt ?? b.createdAt ?? b.updatedAt).getTime());
  }, [data?.nodes]);
  const completedPlaybookNodes = playbookNodes.filter(node => node.playbookNode?.state === 'completed' || node.playbookNode?.state === 'observed' || node.lifecycleState === 'resolved' || node.lifecycleState === 'active');
  const storedPendingNodes = playbookNodes.filter(node => node.playbookNode?.state === 'pending' || node.lifecycleState === 'candidate');
  const storedPendingTitles = new Set(storedPendingNodes.map(node => node.title.toLowerCase()));
  const expectedPendingNodes = (template?.expectedMainNodes ?? []).filter(node => !storedPendingTitles.has(node.title.toLowerCase()));
  const gmailConnectionEvidence = Boolean(data?.staffSetup?.rows.some(row => {
    const status = row.googleWorkspaceIngestion.gmail;
    return row.googleWorkspaceIngestion.gmailConnected && (status.livePollingAvailable || Boolean(status.lastSyncedAt));
  }));
  const calendarConnectionEvidence = (data?.staffSetup?.rows ?? [])
    .filter(row => {
      const status = row.googleWorkspaceIngestion.calendar;
      return row.googleWorkspaceIngestion.calendarConnected && (status.livePollingAvailable || Boolean(status.lastSyncedAt));
    })
    .map(row => row.label);

  if (loading) {
    return <div className="text-sm text-slate-500">Loading communications…</div>;
  }

  if (error) {
    return (
      <SectionCard>
        <div className="text-sm font-medium text-red-700">{error}</div>
        <button onClick={() => void load()} className="mt-4 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
          Retry
        </button>
      </SectionCard>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Communications"
        title="Communications"
        description="Shows the customer, what happened, and the next steps Oracord is waiting for."
        action={(
          <button onClick={() => void load()} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50">
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        )}
      />

      <div className="grid gap-4 md:grid-cols-3">
        <BooleanCard label="Playbook active" value={playbookActive} detail={selectedProfile?.label ?? 'No playbook selected'} />
        <BooleanCard label="Customer steps" value={playbookHelpedSummary || playbookNodes.length > 0} detail={playbookNodes.length > 0 ? `${playbookNodes.length} customer step${playbookNodes.length === 1 ? '' : 's'} saved.` : 'No customer steps saved yet.'} />
        <BooleanCard label="Gmail connected" value={gmailConnectionEvidence} detail={gmailConnectionEvidence ? 'Gmail is connected or has synced before.' : 'No Gmail connection/sync evidence detected.'} />
      </div>

      <SectionCard>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Customer candidate</div>
            <h2 className="mt-2 text-lg font-bold text-slate-950">{profileCandidateDisplay.title}</h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">{profileCandidateDisplay.detail}</p>
          </div>
          <div className="flex flex-col items-start gap-2 lg:items-end">
            <StatusBadge tone={profileCandidateDisplay.tone}>{profileCandidateDisplay.badgeLabel}</StatusBadge>
            {profileCandidateDisplay.availableActions.length > 0 && (
              <div className="flex flex-wrap gap-2 lg:justify-end">
                {profileCandidateDisplay.availableActions.map(action => (
                  <button
                    key={action}
                    type="button"
                    disabled={candidateActionPending !== null}
                    onClick={() => void applyProfileCandidateAction(action)}
                    className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {candidateActionPending === action ? 'Updating…' : profileCandidateActionLabel(action)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        {candidateActionError && <div className="mt-3 text-sm font-medium text-red-700">{candidateActionError}</div>}
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {profileCandidateDisplay.fields.map(field => (
            <div key={field.label} className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
              <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">{field.label}</div>
              <div className="mt-1 text-sm font-semibold text-slate-950">{field.value}</div>
            </div>
          ))}
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
            <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Linked nodes</div>
            <div className="mt-1 text-sm font-semibold text-slate-950">{profileCandidateDisplay.linkedNodesLabel}</div>
          </div>
        </div>
        {profileCandidateDisplay.projectTerms.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {profileCandidateDisplay.projectTerms.map(term => (
              <span key={term} className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700">{term}</span>
            ))}
          </div>
        )}
        {profileCandidateDisplay.evidenceSummary && (
          <p className="mt-4 text-sm leading-6 text-slate-700">{profileCandidateDisplay.evidenceSummary}</p>
        )}
        <p className="mt-3 text-xs leading-5 text-slate-500">{profileCandidateDisplay.safetyLabel}</p>
      </SectionCard>

      <SectionCard>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Customer clues</div>
            <h2 className="mt-2 text-lg font-bold text-slate-950">{profileSummaryDisplay.title}</h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">{profileSummaryDisplay.detail}</p>
          </div>
          <StatusBadge tone={profileConfidenceTone(data?.profileSummary?.contactConfidence.level ?? 'unknown')}>
            {profileSummaryDisplay.confidenceBadge}
          </StatusBadge>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {profileSummaryDisplay.visibleHints.length > 0 ? profileSummaryDisplay.visibleHints.map(hint => (
            <div key={hint.key} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
              <span className="font-semibold text-slate-950">{hint.label}: {hint.value}</span>
              <span className="ml-2 text-slate-500">{hint.detail}</span>
            </div>
          )) : (
            <div className="rounded-xl border border-dashed border-slate-200 px-3 py-2 text-sm text-slate-500">
              No safe customer clues to display yet.
            </div>
          )}
        </div>
        <p className="mt-3 text-xs leading-5 text-slate-500">{profileSummaryDisplay.safetyLabel}</p>
      </SectionCard>

      <SectionCard>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">First communication summary</div>
            <h2 className="mt-2 text-xl font-bold text-slate-950">{firstNode?.title ?? 'No communication node yet'}</h2>
          </div>
          {firstNode && <StatusBadge tone="emerald">Stored</StatusBadge>}
        </div>
        <p className="mt-4 whitespace-pre-wrap text-sm leading-6 text-slate-700">
          {firstNode?.body?.trim() || 'No summary is stored on the first communication node yet.'}
        </p>
      </SectionCard>

      <SectionCard>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Customer journey</div>
            <h2 className="mt-2 text-xl font-bold text-slate-950">{engineJourneyDisplay.title}</h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">{engineJourneyDisplay.detail}</p>
          </div>
          <StatusBadge tone={engineJourneyDisplay.status === 'ready' ? 'emerald' : 'slate'}>
            {engineJourneyDisplay.status === 'ready' ? 'Saved' : 'Waiting'}
          </StatusBadge>
        </div>
        <div className="mt-5 grid gap-3 md:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
            <div className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Done</div>
            <div className="mt-2 text-2xl font-bold text-slate-950">{engineJourneyDisplay.completedCountLabel}</div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
            <div className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Waiting for</div>
            <div className="mt-2 text-2xl font-bold text-slate-950">{engineJourneyDisplay.pendingCountLabel}</div>
          </div>
        </div>
        <div className="mt-5 space-y-3">
          {engineJourneyDisplay.watchItems.length > 0 ? engineJourneyDisplay.watchItems.map(item => (
            <div key={item.key} className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-slate-950">{item.title}</div>
                  <p className="mt-1 text-xs leading-5 text-slate-500">{item.detail}</p>
                </div>
                <StatusBadge tone={item.tone}>{item.statusLabel}</StatusBadge>
              </div>
            </div>
          )) : (
            <div className="rounded-xl border border-dashed border-slate-200 p-4 text-sm text-slate-500">
              No pending next steps to display yet.
            </div>
          )}
        </div>
        <p className="mt-4 text-xs leading-5 text-slate-500">{engineJourneyDisplay.safetyLabel}</p>
      </SectionCard>

      <SectionCard>
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Timeline preview</div>
            <h2 className="mt-2 text-xl font-bold text-slate-950">{template?.label ?? 'No playbook future nodes'}</h2>
          </div>
          <StatusBadge tone="slate">V1 preview</StatusBadge>
        </div>
        <div className="mt-6 space-y-4">
          {completedPlaybookNodes.length > 0 ? completedPlaybookNodes.map(node => (
            <TimelineRow
              key={node.nodeId ?? node.id}
              complete
              title={node.title}
              detail={plainTimelineDetail(node)}
            />
          )) : (
            <TimelineRow
              complete={Boolean(firstNode)}
              title={firstNode?.title ?? 'First communication node'}
              detail={firstNode ? 'Saved from the first communication.' : 'Waiting for the first communication.'}
            />
          )}
          {storedPendingNodes.map(node => (
            <TimelineRow key={node.nodeId ?? node.id} complete={false} title={node.title} detail={watchDetailForNode(node)} />
          ))}
          {expectedPendingNodes.map(node => (
            <TimelineRow key={`${node.kind}:${node.title}`} complete={false} title={node.title} detail={node.dueHint ?? 'Expected future playbook node.'} />
          ))}
          {!storedPendingNodes.length && !expectedPendingNodes.length && (
            <div className="rounded-xl border border-dashed border-slate-200 p-4 text-sm text-slate-500">
              No pending next steps available yet.
            </div>
          )}
        </div>
      </SectionCard>

      <SectionCard>
        <div className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Calendar monitoring</div>
        <div className="mt-3 text-sm text-slate-700">
          {calendarConnectionEvidence.length > 0 ? (
            <ul className="list-disc space-y-1 pl-5">
              {calendarConnectionEvidence.map(label => <li key={label}>{label}</li>)}
            </ul>
          ) : (
            <span>None</span>
          )}
        </div>
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm leading-6 text-amber-900">
          Testing note: the current sync stores events with <span className="font-semibold">source calendar: primary</span>. That means Oracord is polling the signed-in Google account’s primary calendar, not every visible/shared calendar. A full embedded Google Calendar view is not wired in yet.
        </div>
      </SectionCard>
    </div>
  );
}


function profileCandidateActionLabel(action: CommunicationProfileCandidateAction): string {
  if (action === 'confirm') return 'Confirm';
  if (action === 'reject') return 'Reject';
  if (action === 'mark_needs_review') return 'Needs review';
  return 'Reopen as candidate';
}

function BooleanCard({ label, value, detail }: { label: string; value: boolean; detail: string }) {
  return (
    <SectionCard>
      <div className="flex items-start gap-3">
        <div className={`rounded-xl p-2 ${value ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
          {value ? <CheckCircle2 className="h-5 w-5" /> : <Circle className="h-5 w-5" />}
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">{label}</div>
          <div className="mt-1 text-2xl font-bold text-slate-950">{value ? 'Yes' : 'No'}</div>
          <p className="mt-2 text-sm leading-5 text-slate-600">{detail}</p>
        </div>
      </div>
    </SectionCard>
  );
}

function plainTimelineDetail(node: CommunicationNode): string {
  const title = node.title.toLowerCase();
  if (title.includes('call received')) return 'Got a phone call. Voicemail was left.';
  if (title.includes('call returned')) return 'Waiting for the business line to call the customer back.';
  if (title.includes('meeting')) return 'Waiting for a site meeting to be booked.';
  if (title.includes('quote') || title.includes('estimate')) return 'Waiting for the quote or estimate to be sent.';
  return node.playbookNode?.state === 'completed' ? 'Done.' : 'Saved from the communication.';
}

function TimelineRow({ complete, title, detail }: { complete: boolean; title: string; detail: string }) {
  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center">
        <div className={`flex h-9 w-9 items-center justify-center rounded-full border ${complete ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-slate-50 text-slate-400'}`}>
          {complete ? <CheckCircle2 className="h-5 w-5" /> : <Circle className="h-4 w-4" />}
        </div>
        <div className="mt-2 h-8 w-px bg-slate-200" />
      </div>
      <div className={`flex-1 rounded-2xl border p-4 ${complete ? 'border-emerald-200 bg-emerald-50/60' : 'border-slate-200 bg-slate-50/80'}`}>
        <div className="flex items-center gap-2">
          {complete ? <Mail className="h-4 w-4 text-emerald-700" /> : <Workflow className="h-4 w-4 text-slate-400" />}
          <div className="font-semibold text-slate-950">{title}</div>
        </div>
        <p className="mt-2 text-sm leading-5 text-slate-600">{detail}</p>
      </div>
    </div>
  );
}
